// ═══════════════════════════════════════════════════
// SOLOTO — Pont Web3 (Anchor 0.31) v3.0
// ═══════════════════════════════════════════════════
// Toute la logique chaîne vit ici. L'UI (soloto.html) n'appelle que
// les fonctions exportées — elle ne touche jamais directement à Solana.
// Bundlé par app/build.mjs → window.Soloto (cf. étape 2).
//
// Wallet : Phantom uniquement (window.solana). Sur mobile, on rebondit
// vers le navigateur intégré de Phantom (openInPhantom) où window.solana
// est injecté comme sur desktop.

import "./node-shims"; // DOIT rester en premier (Buffer/process avant anchor)
import { Program, AnchorProvider, BN, utils } from "@coral-xyz/anchor";
import {
  PublicKey,
  Connection,
  LAMPORTS_PER_SOL,
  SystemProgram,
} from "@solana/web3.js";
import idl from "../target/idl/soloto.json";
import type { Soloto } from "../target/types/soloto";
import {
  RPC_URL,
  COMMITMENT,
  NETWORK,
  PROGRAM_ID,
  PYTH_SOL_USD,
} from "./config";

// ── Transport RPC discipliné ─────────────────────
// Le RPC public devnet est agressivement rate-limité (429). On fait passer
// TOUT le trafic (Anchor inclus) par un seul point de contrôle :
//   • concurrence bornée + espacement minimal entre départs (anti-rafale),
//   • backoff respectueux sur 429 (un 429 devient un retard, pas une erreur).
// Le jour d'un RPC dédié, ce wrapper reste valable (ceinture de sécurité).
const MAX_IN_FLIGHT = 2; // requêtes simultanées max
const MIN_SPACING_MS = 120; // intervalle minimal entre deux départs
const MAX_RETRIES_429 = 5; // tentatives avant d'abandonner sur un 429
const BACKOFF_BASE_MS = 500; // 0,5s → 1s → 2s → 4s → 8s (plafonné)
const BACKOFF_CAP_MS = 8000;

let _inFlight = 0;
let _lastStart = 0;
const _waiters: Array<() => void> = [];

function _pump(): void {
  if (_inFlight >= MAX_IN_FLIGHT || _waiters.length === 0) return;
  const now = Date.now();
  const wait = Math.max(0, _lastStart + MIN_SPACING_MS - now);
  const next = _waiters.shift()!;
  _lastStart = now + wait;
  _inFlight++;
  setTimeout(next, wait);
}

function _acquire(): Promise<void> {
  return new Promise((resolve) => {
    _waiters.push(resolve);
    _pump();
  });
}

const _sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// fetch throttlé + backoff 429, branché directement dans la Connection.
async function throttledFetch(input: any, init?: any): Promise<Response> {
  await _acquire();
  try {
    for (let attempt = 0; ; attempt++) {
      const res = await fetch(input, init);
      if (res.status !== 429) return res;
      if (attempt >= MAX_RETRIES_429) return res; // on rend le 429 ; l'appelant gère
      const retryAfter = Number(res.headers.get("retry-after"));
      const backoff =
        Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt);
      await _sleep(backoff + Math.random() * 250); // jitter anti-synchronisation
    }
  } finally {
    _inFlight--;
    _pump();
  }
}

export const connection = new Connection(RPC_URL, {
  commitment: COMMITMENT,
  fetch: throttledFetch as any,
});
export { NETWORK, PROGRAM_ID };

// ── PDAs ─────────────────────────────────────────
function configPDA(): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from("config")],
    PROGRAM_ID,
  )[0];
}

function lotteryPDA(drawNumber: number): PublicKey {
  const buf = Buffer.alloc(8);
  buf.writeBigUInt64LE(BigInt(drawNumber));
  return PublicKey.findProgramAddressSync(
    [Buffer.from("lottery"), buf],
    PROGRAM_ID,
  )[0];
}

function vaultPDA(lottery: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from("vault"), lottery.toBuffer()],
    PROGRAM_ID,
  )[0];
}

function playerPDA(lottery: PublicKey, player: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from("player"), lottery.toBuffer(), player.toBuffer()],
    PROGRAM_ID,
  )[0];
}

// ── Wallet Phantom ───────────────────────────────
function getPhantom(): any {
  const p = (window as any).solana;
  if (!p?.isPhantom) {
    throw new Error("Phantom non détecté. Installe-le sur phantom.app");
  }
  return p;
}

// ── Programmes Anchor ────────────────────────────
// Lecture seule : fonctionne AVANT toute connexion wallet (afficher la
// cagnotte, l'historique...). Wallet factice (aucune signature possible).
const READONLY_WALLET = {
  publicKey: PublicKey.default,
  signTransaction: async (t: any) => t,
  signAllTransactions: async (t: any) => t,
};

let _readProgram: Program<Soloto> | null = null;
function readProgram(): Program<Soloto> {
  if (_readProgram) return _readProgram;
  const provider = new AnchorProvider(connection, READONLY_WALLET as any, {
    commitment: COMMITMENT,
  });
  _readProgram = new Program({ ...(idl as any), address: PROGRAM_ID.toString() } as Soloto, provider);
  return _readProgram;
}

// Écriture : exige Phantom connecté. Reconstruit après chaque connect.
let _writeProgram: Program<Soloto> | null = null;
function writeProgram(): Program<Soloto> {
  if (_writeProgram) return _writeProgram;
  const wallet = getPhantom();
  if (!wallet.publicKey) {
    throw new Error("Wallet non connecté. Appelle connect() d'abord.");
  }
  const provider = new AnchorProvider(connection, wallet, {
    commitment: COMMITMENT,
  });
  _writeProgram = new Program({ ...(idl as any), address: PROGRAM_ID.toString() } as Soloto, provider);
  return _writeProgram;
}

function walletKey(): PublicKey {
  const pk = getPhantom().publicKey;
  if (!pk) throw new Error("Wallet non connecté.");
  return pk;
}

// ═══════════════════════════════════════════════════
// MOBILE — rebond vers le navigateur intégré de Phantom
// ═══════════════════════════════════════════════════
export function isMobile(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

// true si window.solana est disponible (desktop avec extension, ou déjà
// dans le navigateur intégré de Phantom).
export function hasInjectedPhantom(): boolean {
  return !!(window as any).solana?.isPhantom;
}

// Rouvre la page courante DANS le navigateur intégré de Phantom, où
// window.solana sera injecté. À appeler quand isMobile() && !hasInjectedPhantom().
export function openInPhantom(): void {
  const url = window.location.href;
  const ref = window.location.origin;
  window.location.href =
    `https://phantom.app/ul/browse/${encodeURIComponent(url)}` +
    `?ref=${encodeURIComponent(ref)}`;
}

// ═══════════════════════════════════════════════════
// 1. CONNEXION WALLET
// ═══════════════════════════════════════════════════
export async function connect(): Promise<{ address: string; balance: number }> {
  const phantom = getPhantom();
  const res = await phantom.connect();
  _writeProgram = null; // forcer la reconstruction avec le wallet connecté
  const pk: PublicKey = res.publicKey ?? phantom.publicKey;
  const balance = await connection.getBalance(pk);
  return { address: pk.toString(), balance: balance / LAMPORTS_PER_SOL };
}

export async function disconnect(): Promise<void> {
  try {
    await getPhantom().disconnect();
  } catch {
    /* ignore */
  }
  _writeProgram = null;
}

// ═══════════════════════════════════════════════════
// 2. LOTERIE EN COURS
// ═══════════════════════════════════════════════════
async function currentLotteryKey(): Promise<PublicKey> {
  const config: any =
    await readProgram().account.globalConfig.fetch(configPDA());
  return config.currentLotteryKey as PublicKey;
}

export async function getCurrentLottery(): Promise<{
  lotteryAddress: string;
  drawNumber: number;
  jackpot: number;
  totalTickets: number;
  participantCount: number;
  priceUsdCents: number;
  maxTickets: number;
  drawTimestamp: number;
  isOpen: boolean;
  drawCommitted: boolean;
  drawExecuted: boolean;
  winner: string;
} | null> {
  try {
    const program = readProgram();
    const key = await currentLotteryKey();
    const l: any = await program.account.lottery.fetch(key);
    return {
      lotteryAddress: key.toString(),
      drawNumber: (l.drawNumber as BN).toNumber(),
      jackpot: (l.jackpot as BN).toNumber() / LAMPORTS_PER_SOL,
      totalTickets: (l.totalTickets as BN).toNumber(),
      participantCount: (l.participantCount as BN).toNumber(),
      priceUsdCents: (l.priceUsdCents as BN).toNumber(),
      maxTickets: (l.maxTickets as BN).toNumber(),
      drawTimestamp: (l.drawTimestamp as BN).toNumber(),
      isOpen: l.isOpen as boolean,
      drawCommitted: l.drawCommitted as boolean,
      drawExecuted: l.drawExecuted as boolean,
      winner: (l.winner as PublicKey).toString(),
    };
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════
// 3. TICKETS DU JOUEUR
// ═══════════════════════════════════════════════════
export async function getPlayerTickets(
  lotteryAddress: string,
  playerAddress?: string,
): Promise<{
  ticketsOwned: number;
  ticketNumbers: number[];
  winChance: number;
} | null> {
  try {
    const program = readProgram();
    const lottery = new PublicKey(lotteryAddress);
    const player = new PublicKey(playerAddress ?? walletKey());
    const p: any = await program.account.playerAccount.fetch(
      playerPDA(lottery, player),
    );
    const l: any = await program.account.lottery.fetch(lottery);
    const total = (l.totalTickets as BN).toNumber();
    const owned = (p.ticketsOwned as BN).toNumber();
    return {
      ticketsOwned: owned,
      ticketNumbers: (p.ticketNumbers as BN[]).slice(0, owned).map((n) => n.toNumber()),
      winChance: total > 0 ? (owned / total) * 100 : 0,
    };
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════
// 4. ACHETER DES TICKETS (tx signée par Phantom)
// ═══════════════════════════════════════════════════
export async function buyTickets(
  quantity: number,
  lotteryAddress?: string,
): Promise<{ signature: string; quantity: number; totalCost: number }> {
  const program = writeProgram();
  const buyer = walletKey();
  const lottery = new PublicKey(lotteryAddress ?? (await currentLotteryKey()));

  // Paramètres lus depuis la chaîne (jamais de constantes hardcodées).
  const l: any = await program.account.lottery.fetch(lottery);
  const maxTickets = (l.maxTickets as BN).toNumber();

  if (!Number.isInteger(quantity) || quantity < 1 || quantity > maxTickets) {
    throw new Error(`Quantité invalide. Entre 1 et ${maxTickets}.`);
  }

  // Vérification solde approximative : estimer le coût via le prix Pyth lisible on-chain.
  // Le contrat calcule le montant exact — ici c'est juste un guard UI.
  const balance = await connection.getBalance(buyer);
  if (balance < 5_000_000) {
    throw new Error(`Solde insuffisant pour acheter des tickets.`);
  }

  // .rpc() confirme déjà (commitment du provider) — pas de confirm redondant.
  const signature = await program.methods
    .buyTickets(new BN(quantity))
    .accountsPartial({
      lottery,
      playerAccount: playerPDA(lottery, buyer),
      vault: vaultPDA(lottery),
      buyer,
      systemProgram: SystemProgram.programId,
      pythSolUsd: PYTH_SOL_USD,
    })
    .rpc();

  // Calculer le coût réel depuis la variation de solde.
  const newBalance = await connection.getBalance(buyer);
  const totalCost = Math.max(0, balance - newBalance) / LAMPORTS_PER_SOL;
  return { signature, quantity, totalCost };
}

// ═══════════════════════════════════════════════════
// 6. VÉRIFIER SI LE WALLET A GAGNÉ
// ═══════════════════════════════════════════════════
// Les gains sont versés AUTOMATIQUEMENT par le contrat lors du reveal_draw.
// Cette fonction sert uniquement à l'affichage (« tu as gagné »).
export async function checkIfWinner(
  lotteryAddress: string,
  playerAddress?: string,
): Promise<{ isWinner: boolean; prizeAmount: number }> {
  const program = readProgram();
  const lottery = new PublicKey(lotteryAddress);
  const l: any = await program.account.lottery.fetch(lottery);
  if (!l.drawExecuted) return { isWinner: false, prizeAmount: 0 };

  try {
    const player = new PublicKey(playerAddress ?? walletKey());
    const p: any = await program.account.playerAccount.fetch(
      playerPDA(lottery, player),
    );
    const winTicket = (l.winningTicket as BN).toNumber();
    const owned = (p.ticketsOwned as BN).toNumber();
    const nums = (p.ticketNumbers as BN[]).slice(0, owned);
    const isWinner = nums.some((n) => n.toNumber() === winTicket);
    const jackpot = (l.jackpot as BN).toNumber();
    const prizeAmount = isWinner ? (jackpot * 90) / 100 / LAMPORTS_PER_SOL : 0;
    return { isWinner, prizeAmount };
  } catch {
    return { isWinner: false, prizeAmount: 0 };
  }
}

// ═══════════════════════════════════════════════════
// 7. ÉVÉNEMENTS TEMPS RÉEL (les 7 events du contrat)
// ═══════════════════════════════════════════════════
export type EventCallbacks = {
  onTicketsPurchased?: (e: any) => void;
  onDrawCommitted?: (e: any) => void;
  onDrawExecuted?: (e: any) => void;
  onDrawRollover?: (e: any) => void;
  onPrizeAutoSent?: (e: any) => void;
  onRefundClaimed?: (e: any) => void;
  onLotteryInitialized?: (e: any) => void;
};

export function subscribeToEvents(cb: EventCallbacks): () => Promise<void> {
  const program = readProgram();
  const ids: number[] = [];
  const on = (name: string, fn?: (e: any) => void) => {
    if (!fn) return;
    ids.push(
      program.addEventListener(name as any, (e: any) => {
        try {
          fn(e);
        } catch {
          /* ne jamais casser le listener */
        }
      }),
    );
  };

  on("TicketsPurchased", cb.onTicketsPurchased);
  on("DrawCommitted", cb.onDrawCommitted);
  on("DrawExecuted", cb.onDrawExecuted);
  on("DrawRollover", cb.onDrawRollover);
  on("PrizeAutoSent", cb.onPrizeAutoSent);
  on("RefundClaimed", cb.onRefundClaimed);
  on("LotteryInitialized", cb.onLotteryInitialized);

  return async () => {
    for (const id of ids) await program.removeEventListener(id);
  };
}

// ═══════════════════════════════════════════════════
// 8. HISTORIQUE DES TIRAGES
// ═══════════════════════════════════════════════════
export async function getDrawHistory(count = 10): Promise<
  Array<{
    drawNumber: number;
    winner: string;
    jackpot: number;
    totalTickets: number;
    drawTimestamp: number;
    rollover: boolean;
    rolloverPlayer: string;
    lotteryAddress: string;
  }>
> {
  const program = readProgram();
  const config: any = await program.account.globalConfig.fetch(configPDA());
  const totalDraws = (config.totalDraws as BN).toNumber();
  const history: any[] = [];
  const start = Math.max(0, totalDraws - count);

  // Lecture groupée : un seul getMultipleAccounts pour toute la fenêtre, au lieu
  // d'un getAccountInfo par tirage (anti-rafale 429).
  const pdas: PublicKey[] = [];
  for (let i = start; i < totalDraws; i++) pdas.push(lotteryPDA(i));
  let accounts: any[] = [];
  try {
    accounts = await program.account.lottery.fetchMultiple(pdas);
  } catch {
    return history; // lecture impossible : on rend une liste vide plutôt que de crasher
  }

  accounts.forEach((l: any, idx: number) => {
    if (!l) return; // tirage manquant : on l'ignore
    // N'inclure que les loteries RÉELLEMENT tirées : la loterie courante (ouverte
    // par open_next mais pas encore tirée) a winner = défaut et un jackpot partiel
    // → sinon elle apparaît en tête d'historique comme un faux « 0 SOL » et la
    // modale gagnant lit ce 0 au lieu du vrai gain. Les rollovers (draw_executed =
    // true) restent inclus.
    if (!(l.drawExecuted as boolean)) return;
    history.push({
      drawNumber: (l.drawNumber as BN).toNumber(),
      winner: (l.winner as PublicKey).toString(),
      jackpot: (l.jackpot as BN).toNumber() / LAMPORTS_PER_SOL,
      totalTickets: (l.totalTickets as BN).toNumber(),
      drawTimestamp: (l.drawTimestamp as BN).toNumber(),
      rollover: l.rollover as boolean,
      // Joueur dont le ticket a été reporté sur CE tirage (rollover solo). Sert au
      // rejeu différé : ce joueur n'a pas de PlayerAccount ici, mais il a bien
      // participé — il doit pouvoir revoir son verdict.
      rolloverPlayer: (l.rolloverPlayer as PublicKey).toString(),
      lotteryAddress: lotteryPDA(start + idx).toString(),
    });
  });
  return history.reverse();
}

// Nombre de tickets possédés par un joueur sur PLUSIEURS loteries, en une seule
// requête (getMultipleAccounts). Sert au rejeu différé : au lieu d'interroger la
// chaîne tirage par tirage, on lit tous les PlayerAccount d'un coup.
// Retourne un tableau aligné sur `lotteryAddresses` (0 si pas de participation).
export async function getPlayerTicketsBatch(
  lotteryAddresses: string[],
  playerAddress?: string,
): Promise<number[]> {
  if (lotteryAddresses.length === 0) return [];
  try {
    const program = readProgram();
    const player = new PublicKey(playerAddress ?? walletKey());
    const pdas = lotteryAddresses.map((a) =>
      playerPDA(new PublicKey(a), player),
    );
    const accs: any[] = await program.account.playerAccount.fetchMultiple(pdas);
    return accs.map((p: any) => (p ? (p.ticketsOwned as BN).toNumber() : 0));
  } catch {
    return lotteryAddresses.map(() => 0);
  }
}

// Statistiques CUMULÉES depuis l'origine, lues on-chain — pas la fenêtre des
// derniers tirages :
//   • totalPrizesPaidSol : config.total_prizes_paid, incrémenté par reveal_draw à
//     chaque paiement de gagnant → somme exacte réellement redistribuée ;
//   • winnerCount : nombre de loteries draw_executed && !rollover, compté par
//     getProgramAccounts filtré (dataSlice vide : seules les clés transitent).
// Offsets memcmp = layout de Lottery (lib.rs) : 8 (discriminateur) + 3×32 (Pubkey)
// + 7×8 (u64/i64) → is_open=160, draw_committed=161, draw_executed=162,
// prize_claimed=163, rollover=164. À resynchroniser si la struct bouge.
// (Validé on-chain le 02/07 : memcmp serveur == filtre client, 26 gagnants devnet.)
export async function getGlobalStats(): Promise<{
  totalPrizesPaidSol: number;
  totalDraws: number;
  winnerCount: number;
}> {
  const program = readProgram();
  const config: any = await program.account.globalConfig.fetch(configPDA());
  const lotteryDisc = (idl as any).accounts.find(
    (a: { name: string }) => a.name === "Lottery",
  ).discriminator;
  const winners = await connection.getProgramAccounts(PROGRAM_ID, {
    dataSlice: { offset: 0, length: 0 }, // compter suffit : aucune data téléchargée
    filters: [
      { memcmp: { offset: 0, bytes: utils.bytes.bs58.encode(Buffer.from(lotteryDisc)) } },
      { memcmp: { offset: 162, bytes: "2" } }, // draw_executed == true  (base58 de [1])
      { memcmp: { offset: 164, bytes: "1" } }, // rollover     == false (base58 de [0])
    ],
  });
  return {
    totalPrizesPaidSol:
      (config.totalPrizesPaid as BN).toNumber() / LAMPORTS_PER_SOL,
    totalDraws: (config.totalDraws as BN).toNumber(),
    winnerCount: winners.length,
  };
}

// Liste RÉELLE des participants d'une loterie, lue on-chain : tous les PlayerAccount
// filtrés par loterie (memcmp sur le champ `lottery`, offset 8+32). Chaque entrée =
// vraie adresse + vrai nombre de tickets. Remplace l'habillage cosmétique (FAKE_WALLETS),
// incohérent avec la transparence on-chain revendiquée. Trié par ordre d'arrivée
// (plus petit numéro de ticket détenu croissant).
export async function getParticipants(
  lotteryAddress: string,
): Promise<{ wallet: string; tickets: number }[]> {
  try {
    const program = readProgram();
    const lottery = new PublicKey(lotteryAddress);
    const accs: any[] = await program.account.playerAccount.all([
      { memcmp: { offset: 8 + 32, bytes: lottery.toBase58() } },
    ]);
    const list = accs
      .map((a: any) => {
        const owned = (a.account.ticketsOwned as BN).toNumber();
        const nums = (a.account.ticketNumbers as BN[])
          .slice(0, owned)
          .map((n) => n.toNumber());
        return {
          wallet: (a.account.wallet as PublicKey).toString(),
          tickets: owned,
          firstTicket: nums.length ? Math.min(...nums) : 0,
        };
      })
      .filter((p) => p.tickets > 0);

    // Joueur reporté (rollover solo) : ses tickets vivent sur le compte Lottery
    // (rollover_player possède la plage [0, rollover_ticket_end)), PAS dans un
    // PlayerAccount — le memcmp ci-dessus le rate donc. On l'ajoute pour qu'il
    // apparaisse comme un participant à part entière. S'il a aussi racheté des
    // tickets ce tour-ci, on FUSIONNE (jamais de double comptage).
    // NB : le report « forcé » multi-joueurs (failed_lottery/rollover_ticket_count)
    // n'est pas couvert ici — cas rare (panne du bot), à traiter séparément.
    try {
      const l: any = await program.account.lottery.fetch(lottery);
      const carried = l.rolloverPlayer as PublicKey;
      const carriedCount = (l.rolloverTicketEnd as BN).toNumber();
      if (carried && !carried.equals(PublicKey.default) && carriedCount > 0) {
        const addr = carried.toString();
        const existing = list.find((p) => p.wallet === addr);
        if (existing) {
          existing.tickets += carriedCount;
          existing.firstTicket = 0; // possède la plage reportée, arrivé « en premier »
        } else {
          list.push({ wallet: addr, tickets: carriedCount, firstTicket: 0 });
        }
      }
    } catch {
      /* pas de report lisible : liste inchangée */
    }

    return list
      .sort((a, b) => a.firstTicket - b.firstTicket)
      .map(({ wallet, tickets }) => ({ wallet, tickets }));
  } catch {
    return [];
  }
}
